// Pan bounds for the orbit camera: where the orbit TARGET may wander.
// Pure maths (no THREE); CameraPanClamp.tsx applies it every frame.
//
// The playable board's world extent (tile centres) plus a margin. A SeaFrame's
// extra ocean rings (or a landlocked mist curtain) widen the margin so the
// player can still look at the ring / wall. The clamp is *soft*: a target that
// has been dragged past the box is eased back (exponential), never snapped, so
// panning against the edge feels like a rubber band.

export interface Extent {
  readonly minX: number;
  readonly maxX: number;
  readonly minZ: number;
  readonly maxZ: number;
}

/** World units of slack past the outermost tile centres on every side (~1 hex). */
export const BASE_MARGIN = 2;
/** Width of one hex ring in world units (pointy-top, circumradius 1). */
export const RING_WIDTH = Math.sqrt(3);
/** Share of each sea ring the target may reach: enough to frame the ring without losing the board. */
export const SEA_MARGIN_SHARE = 0.5;
/** Easing rate (1/s) of the pull-back; ~0.1 s time constant. */
export const PULL_RATE = 10;

export interface Positioned {
  readonly transform: { readonly position: readonly number[] };
}

/** Tile-centre extent of a set of world positions, or null when empty. */
export function tileExtent(tiles: ReadonlyArray<Positioned>): Extent | null {
  if (tiles.length === 0) return null;
  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let minZ = Number.POSITIVE_INFINITY;
  let maxZ = Number.NEGATIVE_INFINITY;
  for (const t of tiles) {
    const x = t.transform.position[0] ?? 0;
    const z = t.transform.position[2] ?? 0;
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (z < minZ) minZ = z;
    if (z > maxZ) maxZ = z;
  }
  return { minX, maxX, minZ, maxZ };
}

/**
 * The allowed target box: `extent` grown by the base margin plus `seaRings`
 * hex rings, all multiplied by the Large Board `scale` (world content renders
 * at `scale`, the board-space extent is unscaled).
 */
export function panBounds(extent: Extent, seaRings = 0, scale = 1): Extent {
  const m = BASE_MARGIN + Math.max(0, seaRings) * RING_WIDTH * SEA_MARGIN_SHARE;
  return {
    minX: (extent.minX - m) * scale,
    maxX: (extent.maxX + m) * scale,
    minZ: (extent.minZ - m) * scale,
    maxZ: (extent.maxZ + m) * scale,
  };
}

/** Nearest point of the box to (x, z). */
export function clampToBounds(x: number, z: number, b: Extent): [number, number] {
  return [Math.min(b.maxX, Math.max(b.minX, x)), Math.min(b.maxZ, Math.max(b.minZ, z))];
}

/**
 * Soft pull: the (dx, dz) to ADD to the target (and the camera) this frame so
 * it eases toward the box. [0, 0] when inside, or when dt is not positive.
 */
export function softPullDelta(
  x: number,
  z: number,
  b: Extent,
  dtSeconds: number,
  rate = PULL_RATE,
): [number, number] {
  if (!(dtSeconds > 0)) return [0, 0];
  const [cx, cz] = clampToBounds(x, z, b);
  const k = 1 - Math.exp(-rate * dtSeconds);
  // Below a hair, finish the move so the camera does not creep asymptotically.
  const pull = (from: number, to: number) =>
    Math.abs(to - from) < 1e-4 ? to - from : (to - from) * k;
  return [pull(x, cx), pull(z, cz)];
}
